import React, { useState, useEffect, useRef } from "react";
import { useDetectOutsideClick } from "../../hooks/useDetectOutsideClick";
import {
  getProjectLabelsAPI,
  addProjectLabelAPI,
  removeProjectLabelAPI,
  updateLabelNameAPI,
} from "../../utils/requests/projects";
import { toastSuccess, toastError } from "../../utils/helpers/helper";
import { FaPencilAlt } from "react-icons/fa";
import "./ProjectLabelDropdown.css";

const DEFAULT_LABEL_COLORS = [
  { key: "green", name: "Unresponsive", rgb: "rgb(78, 205, 151)" },
  { key: "yellow", name: "Neutral Interest", rgb: "rgb(244, 208, 63)" },
  { key: "orange", name: "High Interest", rgb: "rgb(243, 156, 18)" },
  { key: "red", name: "GC Not Awarded", rgb: "rgb(231, 76, 60)" },
  { key: "purple", name: "Site Visit (Scheduled/Made)", rgb: "rgb(155, 89, 182)" },
  { key: "blue", name: "Pricing Negotiations", rgb: "rgb(52, 152, 219)" },
  { key: "pink", name: "Awarded", rgb: "rgb(232, 67, 147)" },
  { key: "teal", name: "Lost after Negotiations", rgb: "rgb(0, 206, 201)" },
];

const ProjectLabelDropdown = ({ projectId, disabled = false }) => {
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useDetectOutsideClick(dropdownRef, false);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [labelColors, setLabelColors] = useState(DEFAULT_LABEL_COLORS);
  const [loading, setLoading] = useState(false);
  const [editingColor, setEditingColor] = useState(null);
  const [editValue, setEditValue] = useState("");
  const editInputRef = useRef(null);

  useEffect(() => {
    if (projectId) {
      fetchLabels();
    }
  }, [projectId]);

  useEffect(() => {
    if (editingColor && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingColor]);

  const fetchLabels = async () => {
    try {
      const response = await getProjectLabelsAPI(projectId);
      const labels = response?.labels || [];
      setSelectedLabels(labels.map((label) => label.color));
      
      if (response?.color_names) {
        const updatedColors = DEFAULT_LABEL_COLORS.map((color) => ({
          ...color,
          name: response.color_names[color.key] || color.name,
        }));
        setLabelColors(updatedColors);
      }
    } catch (error) {
      console.error("Failed to fetch labels:", error);
    }
  };

  const handleToggleLabel = async (colorKey, e) => {
    if (e) e.stopPropagation();
    if (loading || disabled || editingColor) return;

    setLoading(true);
    const isSelected = selectedLabels.includes(colorKey);

    try {
      if (isSelected) {
        await removeProjectLabelAPI(projectId, colorKey);
        setSelectedLabels((prev) => prev.filter((c) => c !== colorKey));
        toastSuccess("Label removed");
      } else {
        await addProjectLabelAPI(projectId, colorKey);
        setSelectedLabels((prev) => [...prev, colorKey]);
        toastSuccess("Label added");
      }
    } catch (error) {
      toastError("Failed to update label");
      console.error("Failed to toggle label:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      setEditingColor(null);
    }
  };

  const handleStartEdit = (colorKey, currentName, e) => {
    e.stopPropagation();
    setEditingColor(colorKey);
    setEditValue(currentName);
  };

  const handleSaveEdit = async (e) => {
    if (e) e.stopPropagation();
    if (!editValue.trim() || !editingColor) {
      setEditingColor(null);
      return;
    }

    try {
      await updateLabelNameAPI(editingColor, editValue.trim());
      setLabelColors((prev) =>
        prev.map((color) =>
          color.key === editingColor
            ? { ...color, name: editValue.trim() }
            : color
        )
      );
      toastSuccess("Label name updated");
    } catch (error) {
      toastError("Failed to update label name");
      console.error("Failed to update label name:", error);
    } finally {
      setEditingColor(null);
    }
  };

  const handleCancelEdit = (e) => {
    if (e) e.stopPropagation();
    setEditingColor(null);
    setEditValue("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSaveEdit(e);
    } else if (e.key === "Escape") {
      handleCancelEdit(e);
    }
  };

  const getSelectedLabels = () => {
    return labelColors.filter((color) => selectedLabels.includes(color.key));
  };

  const getSelectedColorDots = () => {
    return getSelectedLabels().map((color) => (
      <span
        key={color.key}
        className="label-dot"
        style={{ backgroundColor: color.rgb }}
        title={color.name}
      />
    ));
  };

  return (
    <div className="project-label-wrapper">
      <div className="selected-labels-display">
        {getSelectedLabels().map((color) => (
          <span
            key={color.key}
            className="selected-label-tag"
            style={{ 
              backgroundColor: color.rgb,
              color: ['yellow', 'teal'].includes(color.key) ? '#333' : '#fff'
            }}
          >
            {color.name}
          </span>
        ))}
      </div>
      <div className="project-label-dropdown" ref={dropdownRef}>
        <button
          className={`label-trigger-btn ${disabled ? "disabled" : ""}`}
          onClick={toggleDropdown}
          disabled={disabled}
        >
          <span className="label-text">Tags</span>
          <span className="selected-dots">{getSelectedColorDots()}</span>
          <span className="dropdown-arrow">{isOpen ? "×" : "▼"}</span>
        </button>

        {isOpen && (
          <div className="label-dropdown-menu">
            <div className="label-dropdown-header">
              <span>Select Tags</span>
              <button className="close-btn" onClick={() => setIsOpen(false)}>
                ×
              </button>
            </div>
            <div className="label-options">
              {labelColors.map((color) => {
                const isSelected = selectedLabels.includes(color.key);
                const isEditing = editingColor === color.key;
                return (
                  <div
                    key={color.key}
                    className={`label-option ${isSelected ? "selected" : ""} ${
                      loading ? "loading" : ""
                    } ${isEditing ? "editing" : ""}`}
                    onClick={() => !isEditing && handleToggleLabel(color.key)}
                  >
                    <span
                      className="color-indicator"
                      style={{ backgroundColor: color.rgb }}
                    />
                    {isEditing ? (
                      <div className="edit-name-container" onClick={(e) => e.stopPropagation()}>
                        <input
                          ref={editInputRef}
                          type="text"
                          className="edit-name-input"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={handleKeyDown}
                          maxLength={50}
                        />
                        <button className="edit-save-btn" onClick={handleSaveEdit}>
                          ✓
                        </button>
                        <button className="edit-cancel-btn" onClick={handleCancelEdit}>
                          ✕
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="color-name">{color.name}</span>
                        <button
                          className="edit-name-btn"
                          onClick={(e) => handleStartEdit(color.key, color.name, e)}
                          title="Edit label name"
                        >
                          <FaPencilAlt />
                        </button>
                        <span className="checkbox">{isSelected ? "✓" : ""}</span>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectLabelDropdown;
